import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { PageTitle } from '../components/RouteTitle';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  ShoppingBag, MapPin, CreditCard, ArrowLeft, Plus, Check,
  Trash2, Wallet, Banknote, Loader2, AlertTriangle, ChevronDown,
  ChevronRight, Lock,
} from 'lucide-react';
import { initMercadoPago, Payment } from '@mercadopago/sdk-react';
import { ordersAPI, walletAPI, authAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import { getImageUrl, formatPrice } from '../utils/helpers';
import { getCachedBuyerLocation } from '../hooks/useBuyerLocation';
import { CheckoutContact } from '../components/checkout/CheckoutContact';
import { newCheckoutKey, saveGuestOrder, apiErrorMessage } from '../utils/guestOrders';
import {
  trackBeginCheckout,
  trackAddShippingInfo,
  trackAddPaymentInfo,
  trackPurchase,
} from '../utils/analytics';

const MEXICO_STATES = [
  "Aguascalientes","Baja California","Baja California Sur","Campeche","Chiapas",
  "Chihuahua","Ciudad de México","Coahuila","Colima","Durango","Estado de México",
  "Guanajuato","Guerrero","Hidalgo","Jalisco","Michoacán","Morelos","Nayarit",
  "Nuevo León","Oaxaca","Puebla","Querétaro","Quintana Roo","San Luis Potosí",
  "Sinaloa","Sonora","Tabasco","Tamaulipas","Tlaxcala","Veracruz","Yucatán","Zacatecas"
];

const emptyAddr = {
  recipient: '', phone: '', street: '', neighborhood: '',
  zip_code: '', city: '', state: '', country: 'México',
};

let mpInitialized = false;

const STEP_SHIPPING = 'shipping';
const STEP_PAYMENT = 'payment';

const Checkout = () => {
  const { cart, getCartTotal, clearCart } = useCart();
  const { user, login } = useAuth();
  const [email, setEmail] = useState(user?.email || '');
  const [createAccount, setCreateAccount] = useState(false);
  const [password, setPassword] = useState('');
  const [accountExists, setAccountExists] = useState(false);
  const submitting = useRef(false);
  const navigate = useNavigate();

  // Step state
  const [step, setStep] = useState(STEP_SHIPPING);
  const [confirmedAddress, setConfirmedAddress] = useState(null);

  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [mpActive, setMpActive] = useState(false);
  const [brickReady, setBrickReady] = useState(false);
  const [isCardActive, setIsCardActive] = useState(true);
  const [isWalletActive, setIsWalletActive] = useState(true);
  const [isTransferActive, setIsTransferActive] = useState(true);

  // Address
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newAddr, setNewAddr] = useState({ ...emptyAddr, recipient: user?.name || '' });
  const [addrLoading, setAddrLoading] = useState(true);

  const total = getCartTotal();

  // Fire begin_checkout once on mount
  useEffect(() => {
    if (cart.length > 0) {
      trackBeginCheckout(cart, total);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAddresses = useCallback(async () => {
    if (!user) { setSavedAddresses([]); setShowNewForm(true); setAddrLoading(false); return; }
    try {
      const data = await authAPI.getAddresses();
      setSavedAddresses(data);
      if (data.length > 0) { setSelectedAddressId(data[0].id); setShowNewForm(false); }
      else setShowNewForm(true);
    } catch { setShowNewForm(true); }
    finally { setAddrLoading(false); }
  }, [user]);

  useEffect(() => { if (user?.email) setEmail(user.email); }, [user]);

  useEffect(() => {
    loadAddresses();
    const loadPaymentInfo = async () => {
      try {
        const [walletData, mpData] = await Promise.all([
          user ? walletAPI.getWallet().catch(() => ({ balance: 0 })) : Promise.resolve({ balance: 0 }),
          walletAPI.getMPPublicKey().catch(() => ({ public_key: null, active: false })),
        ]);
        setWalletBalance(walletData.balance || 0);
        const cardOn = mpData.is_card_active !== false;
        const walletOn = !!user && mpData.is_wallet_active !== false;
        const transferOn = mpData.is_transfer_active !== false;
        setIsCardActive(cardOn);
        setIsWalletActive(walletOn);
        setIsTransferActive(transferOn);

        if (mpData.public_key && mpData.active) {
          setMpActive(true);
          if (!mpInitialized) {
            initMercadoPago(mpData.public_key, { locale: 'es-MX' });
            mpInitialized = true;
          }
          setBrickReady(true);
        }
        if (cardOn && mpData.public_key && mpData.active) setPaymentMethod('card');
        else if (transferOn) setPaymentMethod('p2p');
        else if (walletOn) setPaymentMethod('wallet');
      } catch { /* silent */ }
    };
    loadPaymentInfo();
  }, [loadAddresses, user]);

  const getSelectedAddress = () => {
    if (showNewForm) return null;
    return savedAddresses.find(a => a.id === selectedAddressId);
  };

  const validateNewAddr = () => {
    const { recipient, phone, street, neighborhood, zip_code, city, state } = newAddr;
    if (!recipient || !phone || !street || !neighborhood || !zip_code || !city || !state) {
      toast({ title: 'Error', description: 'Completa todos los campos de la dirección', variant: 'destructive' });
      return false;
    }
    return true;
  };

  // Check if address form is complete (for button enable/disable)
  const isNewAddrComplete = () => {
    const { recipient, phone, street, neighborhood, zip_code, city, state } = newAddr;
    return [recipient, phone, street, neighborhood, city, state].every(v => v.trim()) && /^\d{5}$/.test(zip_code);
  };

  const canContinueToPayment = () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return false;
    if (!user && createAccount && !accountExists && password.length < 8) return false;
    if (showNewForm) return isNewAddrComplete();
    return !!selectedAddressId;
  };

  const handleDeleteAddr = async (id) => {
    try {
      await authAPI.deleteAddress(id);
      setSavedAddresses(prev => prev.filter(a => a.id !== id));
      if (selectedAddressId === id) {
        const rem = savedAddresses.filter(a => a.id !== id);
        if (rem.length) setSelectedAddressId(rem[0].id);
        else { setSelectedAddressId(null); setShowNewForm(true); }
      }
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const resolveAddress = async () => {
    if (showNewForm) {
      if (!validateNewAddr()) return null;
      if (!user) return newAddr;
      try {
        const saved = await authAPI.addAddress(newAddr);
        setSavedAddresses(prev => [...prev, saved]);
        return saved;
      } catch (err) {
        toast({ title: 'Error', description: apiErrorMessage(err), variant: 'destructive' });
        return null;
      }
    }
    const addr = getSelectedAddress();
    if (!addr) { toast({ title: 'Selecciona una dirección', variant: 'destructive' }); return null; }
    return addr;
  };

  // Advance to payment step
  const handleContinueToPayment = async () => {
    if (!canContinueToPayment() || loading) return;
    setLoading(true);
    const addr = await resolveAddress();
    setLoading(false);
    if (!addr) return;
    setConfirmedAddress(addr);
    setStep(STEP_PAYMENT);
    // GA4: add_shipping_info
    trackAddShippingInfo(cart, total, addr);
  };

  const buildPayload = (addr, extra = {}) => {
    const items = cart.map(item => ({
      product_id: item.id, product_title: item.title, product_image: item.image,
      quantity: item.quantity, price: item.price, seller_id: item.seller_id, seller_name: item.seller,
      variant_sku: item.variant_sku || null, variant_label: item.variant_label || null,
    }));
    const fingerprint = JSON.stringify([items, total, email.trim().toLowerCase(), paymentMethod, addr]);
    let attempt;
    try { attempt = JSON.parse(sessionStorage.getItem('zl_checkout_attempt') || 'null'); } catch { attempt = null; }
    if (!attempt || attempt.fingerprint !== fingerprint) {
      attempt = { fingerprint, key: newCheckoutKey() };
      sessionStorage.setItem('zl_checkout_attempt', JSON.stringify(attempt));
    }
    return {
      items, total, email: email.trim().toLowerCase(), checkout_key: attempt.key,
      ...(!user && createAccount && !accountExists ? { password } : {}),
      shipping_address: {
        name: addr.recipient,
        address: addr.street,
        neighborhood: addr.neighborhood,
        city: addr.city,
        zip_code: addr.zip_code,
        email: email.trim().toLowerCase(),
        phone: addr.phone,
        state: addr.state,
        ...(getCachedBuyerLocation() ? { lat: getCachedBuyerLocation().lat, lng: getCachedBuyerLocation().lng } : {}),
      },
      payment_method: paymentMethod,
      ...extra,
    };
  };

  const handleOrderSuccess = (order) => {
    if (order.guest && order.guest_access_token) saveGuestOrder(order, order.guest_access_token);
    if (order.auth) { localStorage.setItem('zl_token', order.auth.token); login(order.auth.user); }
    sessionStorage.removeItem('zl_checkout_attempt');
    if (order.payment_info?.status === 'confirmed') trackPurchase(order);
    clearCart();
    const { auth, guest_access_token, ...safeOrder } = order;
    setPassword('');
    navigate('/thank-you', { state: { order: safeOrder } });
  };

  // Wallet / P2P submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step !== STEP_PAYMENT || paymentMethod === 'card') return;
    if (submitting.current) return;
    if (!confirmedAddress) { setStep(STEP_SHIPPING); return; }

    // GA4: add_payment_info
    trackAddPaymentInfo(cart, total, paymentMethod);

    submitting.current = true;
    setLoading(true);
    try {
      const order = await ordersAPI.create(buildPayload(confirmedAddress));
      handleOrderSuccess(order);
    } catch (error) {
      toast({ title: 'Error', description: apiErrorMessage(error), variant: 'destructive' });
    } finally { submitting.current = false; setLoading(false); }
  };

  // Card Brick callback
  const handleCardBrickSubmit = async (formData) => {
    if (submitting.current) return;
    if (!confirmedAddress) { setStep(STEP_SHIPPING); return; }

    trackAddPaymentInfo(cart, total, 'card');

    submitting.current = true;
    setLoading(true);
    try {
      const order = await ordersAPI.create(buildPayload(confirmedAddress, {
        token: formData.token,
        payment_method_id: formData.payment_method_id,
        installments: Number(formData.installments) || 1,
        issuer_id: formData.issuer_id || null,
        payer_email: email.trim().toLowerCase(),
      }));
      handleOrderSuccess(order);
    } catch (error) {
      toast({ title: 'Error', description: apiErrorMessage(error), variant: 'destructive' });
    } finally { submitting.current = false; setLoading(false); }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Card className="p-12 text-center max-w-md">
          <ShoppingBag className="w-24 h-24 mx-auto text-gray-300 mb-4" />
          <h2 className="text-2xl font-bold mb-2">Tu carrito esta vacio</h2>
          <p className="text-gray-600 mb-6">Agrega productos para continuar</p>
          <Button onClick={() => navigate('/')} className="bg-orange-500 hover:bg-orange-600 text-white">Ir a comprar</Button>
        </Card>
      </div>
    );
  }

  const canPayWallet = walletBalance >= total;
  const noMethods = !isCardActive && !isWalletActive && !isTransferActive;

  const MethodOption = ({ id, icon: Icon, title, subtitle, disabled, children }) => {
    const selected = paymentMethod === id;
    return (
      <div className={`border-2 rounded-xl transition-all ${selected ? 'border-orange-500 bg-orange-50/50' : disabled ? 'border-gray-100 opacity-50' : 'border-gray-200 hover:border-gray-300'}`}>
        <button type="button" disabled={disabled}
          onClick={() => setPaymentMethod(id)}
          className="w-full flex items-center gap-4 p-4 text-left"
          data-testid={`payment-method-${id}`}
        >
          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${selected ? 'border-orange-500' : 'border-gray-300'}`}>
            {selected && <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />}
          </div>
          <Icon className={`w-6 h-6 ${selected ? 'text-orange-600' : 'text-gray-400'}`} />
          <div className="flex-1">
            <span className="font-semibold text-sm block">{title}</span>
            <span className="text-[11px] text-gray-400">{subtitle}</span>
          </div>
          {children && selected && <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>
        {selected && children && <div className="px-4 pb-4">{children}</div>}
      </div>
    );
  };

  /* ─── Stepper Header ─── */
  const StepIndicator = () => (
    <div className="flex items-center gap-2 mb-6" data-testid="checkout-stepper">
      <button
        type="button"
        disabled={loading}
        onClick={() => setStep(STEP_SHIPPING)}
        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
          step === STEP_SHIPPING
            ? 'bg-orange-500 text-white shadow-md'
            : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
        }`}
        data-testid="step-shipping"
      >
        <MapPin className="w-4 h-4" />
        <span>1. Envío</span>
      </button>
      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
      <div
        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
          step === STEP_PAYMENT
            ? 'bg-orange-500 text-white shadow-md'
            : confirmedAddress
              ? 'bg-orange-100 text-orange-700'
              : 'bg-gray-100 text-gray-400'
        }`}
        data-testid="step-payment"
      >
        <Lock className="w-4 h-4" />
        <span>2. Pago</span>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 py-8" data-testid="checkout-page">
      <PageTitle title={step === STEP_SHIPPING ? 'Información de envío' : 'Pago seguro'} />
      <div className="max-w-6xl mx-auto px-4">
        <Button data-testid="checkout-back" variant="ghost" disabled={loading} onClick={() => step === STEP_SHIPPING ? navigate('/cart') : setStep(STEP_SHIPPING)} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> {step === STEP_SHIPPING ? 'Volver al carrito' : 'Volver a envío'}
        </Button>
        <h1 className="text-3xl font-bold mb-2">Finalizar compra</h1>
        <StepIndicator />

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              {/* ── STEP 1: Shipping ── */}
              {step === STEP_SHIPPING && (
                <Card className="p-6" data-testid="shipping-section">
                  <CheckoutContact user={user} email={email} setEmail={setEmail} createAccount={createAccount} setCreateAccount={setCreateAccount} password={password} setPassword={setPassword} exists={accountExists} setExists={setAccountExists} />
                  <h2 className="text-xl font-bold mb-4 flex items-center">
                    <MapPin className="w-5 h-5 mr-2 text-orange-500" /> Dirección de envío
                  </h2>
                  {addrLoading ? <p className="text-gray-500 py-4 text-center">Cargando...</p> : (
                    <>
                      {savedAddresses.length > 0 && (
                        <div className="space-y-3 mb-4" data-testid="saved-addresses">
                          {savedAddresses.map(addr => (
                            <div key={addr.id}
                              onClick={() => { setSelectedAddressId(addr.id); setShowNewForm(false); }}
                              className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all ${!showNewForm && selectedAddressId === addr.id ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}
                            >
                              {!showNewForm && selectedAddressId === addr.id && (
                                <div className="absolute top-3 right-3 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                                  <Check className="w-4 h-4 text-white" />
                                </div>
                              )}
                              <p className="font-semibold">{addr.recipient}</p>
                              <p className="text-sm text-gray-600">{addr.street}, {addr.neighborhood}</p>
                              <p className="text-sm text-gray-600">{addr.city}, {addr.state} {addr.zip_code}</p>
                              <p className="text-sm text-gray-500">{addr.phone}</p>
                              <Button type="button" variant="ghost" size="sm"
                                className="absolute bottom-2 right-2 text-gray-400 hover:text-red-500"
                                onClick={(e) => { e.stopPropagation(); handleDeleteAddr(addr.id); }}
                              ><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          ))}
                        </div>
                      )}
                      {!showNewForm && (
                        <Button type="button" variant="outline"
                          onClick={() => { setShowNewForm(true); setSelectedAddressId(null); }}
                          className="w-full border-dashed border-2" data-testid="add-new-address-btn"
                        ><Plus className="w-4 h-4 mr-2" /> Agregar nueva dirección</Button>
                      )}
                      {showNewForm && (
                        <div className="space-y-4 border-2 border-orange-500 rounded-lg p-4 bg-orange-50/30" data-testid="new-address-form">
                          <div className="flex items-center justify-between">
                            <p className="font-semibold text-sm text-orange-600">Nueva dirección</p>
                            {savedAddresses.length > 0 && (
                              <Button type="button" variant="ghost" size="sm"
                                onClick={() => { setShowNewForm(false); setSelectedAddressId(savedAddresses[0].id); }}
                              >Cancelar</Button>
                            )}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div><Label>Destinatario *</Label><Input value={newAddr.recipient} data-testid="addr-recipient" onChange={e => setNewAddr(p => ({...p, recipient: e.target.value}))} placeholder="Nombre completo" /></div>
                            <div><Label>Teléfono *</Label><Input value={newAddr.phone} data-testid="addr-phone" onChange={e => setNewAddr(p => ({...p, phone: e.target.value}))} placeholder="+52 55 1234 5678" /></div>
                            <div className="md:col-span-2"><Label>Calle y número *</Label><Input value={newAddr.street} data-testid="addr-street" onChange={e => setNewAddr(p => ({...p, street: e.target.value}))} placeholder="Av. Reforma 123" /></div>
                            <div><Label>Colonia *</Label><Input value={newAddr.neighborhood} data-testid="addr-neighborhood" onChange={e => setNewAddr(p => ({...p, neighborhood: e.target.value}))} placeholder="Col. Centro" /></div>
                            <div><Label>Código Postal *</Label><Input value={newAddr.zip_code} maxLength={5} data-testid="addr-zipcode" onChange={e => setNewAddr(p => ({...p, zip_code: e.target.value.replace(/\D/g, '').slice(0,5)}))} placeholder="06600" /></div>
                            <div><Label>Ciudad *</Label><Input value={newAddr.city} data-testid="addr-city" onChange={e => setNewAddr(p => ({...p, city: e.target.value}))} placeholder="Ciudad de México" /></div>
                            <div><Label>Estado *</Label>
                              <select value={newAddr.state} onChange={e => setNewAddr(p => ({...p, state: e.target.value}))}
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="addr-state">
                                <option value="">Selecciona un estado</option>
                                {MEXICO_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                              </select>
                            </div>
                            <div><Label>País</Label><Input value="México" disabled className="bg-gray-100" /></div>
                          </div>
                        </div>
                      )}

                      {/* Continue to Payment CTA */}
                      <Button
                        type="button"
                        onClick={handleContinueToPayment}
                        disabled={loading || !canContinueToPayment()}
                        className="w-full mt-6 bg-orange-500 hover:bg-orange-600 h-12 text-lg text-white disabled:opacity-40"
                        data-testid="continue-to-payment-btn"
                      >
                        Continuar al pago <ChevronRight className="w-5 h-5 ml-2" />
                      </Button>
                    </>
                  )}
                </Card>
              )}

              {/* ── STEP 2: Payment ── */}
              {step === STEP_PAYMENT && (
                <>
                  {/* Confirmed address summary */}
                  {confirmedAddress && (
                    <Card className="p-4 border-green-200 bg-green-50/50" data-testid="confirmed-address-summary">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <p className="font-semibold text-sm">{confirmedAddress.recipient}</p>
                            <p className="text-xs text-gray-500">{confirmedAddress.street}, {confirmedAddress.city}, {confirmedAddress.state}</p>
                          </div>
                        </div>
                          <Button type="button" variant="ghost" size="sm" disabled={loading} onClick={() => setStep(STEP_SHIPPING)} className="text-orange-600 hover:text-orange-700" data-testid="checkout-change-address">
                          Cambiar
                        </Button>
                      </div>
                    </Card>
                  )}

                  <Card className="p-6" data-testid="payment-section">
                    <h2 className="text-xl font-bold mb-4 flex items-center">
                      <CreditCard className="w-5 h-5 mr-2 text-orange-500" /> Método de pago
                    </h2>

                    {noMethods ? (
                      <div className="text-center py-6 bg-amber-50 rounded-lg border border-amber-200">
                        <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                        <p className="text-gray-700 font-medium">No hay métodos de pago habilitados</p>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-3">
                        {isCardActive && (
                          <MethodOption id="card" icon={CreditCard} title="Tarjeta de crédito / débito"
                            subtitle={mpActive ? 'Pago seguro con MercadoPago' : 'MercadoPago no configurado'}
                            disabled={!mpActive}
                          >
                            {brickReady && (
                              <div data-testid="mp-payment-brick">
                                <Payment
                                  initialization={{ amount: total, payer: { email: email.trim().toLowerCase() } }}
                                  customization={{ paymentMethods: { creditCard: 'all', debitCard: 'all' }, visual: { style: { theme: 'default' } } }}
                                  onSubmit={async ({ formData }) => { await handleCardBrickSubmit(formData); }}
                                  onError={(err) => { console.error('MP Brick:', err); toast({ title: 'Error en formulario de pago', variant: 'destructive' }); }}
                                />
                              </div>
                            )}
                            {!mpActive && (
                              <p className="text-amber-600 text-xs flex items-center gap-1 mt-1">
                                <AlertTriangle className="w-3 h-3" /> El admin debe configurar MercadoPago
                              </p>
                            )}
                          </MethodOption>
                        )}

                        {user && isWalletActive && (
                          <MethodOption id="wallet" icon={Wallet} title="Billetera Digital"
                            subtitle={`Saldo disponible: ${formatPrice(walletBalance)}`}
                          >
                            <div className={`p-3 rounded-lg border ${canPayWallet ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-sm text-gray-600">Tu saldo</p>
                                  <p className={`text-xl font-black ${canPayWallet ? 'text-green-600' : 'text-red-500'}`}>{formatPrice(walletBalance)}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-gray-500">Total</p>
                                  <p className="text-lg font-bold">{formatPrice(total)}</p>
                                </div>
                              </div>
                              {!canPayWallet && (
                                <p className="mt-2 text-red-600 text-xs flex items-center gap-1">
                                  <AlertTriangle className="w-3 h-3" /> Necesitas {formatPrice(total - walletBalance)} más
                                </p>
                              )}
                            </div>
                          </MethodOption>
                        )}

                        {isTransferActive && (
                          <MethodOption id="p2p" icon={Banknote} title="Transferencia bancaria"
                            subtitle="Recibirás los datos del vendedor tras confirmar"
                          >
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                              <p className="text-blue-600 text-sm">
                                Al confirmar, recibirás los datos bancarios para realizar la transferencia.
                              </p>
                            </div>
                          </MethodOption>
                        )}
                      </div>
                    )}
                  </Card>
                </>
              )}
            </div>

            {/* ── Summary ── */}
            <div>
              <Card className="p-6 sticky top-24" data-testid="order-summary">
                <h2 className="text-xl font-bold mb-4">Resumen</h2>
                <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                  {cart.map(item => (
                    <div key={item.key || item.id} className="flex gap-3" data-testid={`checkout-item-${item.key || item.id}`}>
                      <img src={getImageUrl(item.image)} alt={item.title}
                        className="w-16 h-16 object-cover rounded"
                        onError={e => { e.target.src = 'https://via.placeholder.com/64'; }} />
                      <div className="flex-1">
                        <p className="font-medium text-sm line-clamp-2">{item.title}</p>
                        {item.variant_label && <p className="text-xs text-gray-500">{item.variant_label}</p>}
                        <p className="text-gray-500 text-xs">Cant: {item.quantity}</p>
                        <p className="font-semibold">{formatPrice(item.price * item.quantity)}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between"><span className="text-gray-600">Subtotal</span><span>{formatPrice(total)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Envío</span><span className="text-green-600">Gratis</span></div>
                  <div className="flex justify-between text-xl font-bold border-t pt-2">
                    <span>Total</span><span className="text-orange-500" data-testid="checkout-total">{formatPrice(total)}</span>
                  </div>
                </div>

                {/* Submit button only on payment step */}
                {step === STEP_PAYMENT && paymentMethod !== 'card' && (
                  <Button type="submit"
                    className="w-full mt-6 bg-orange-500 hover:bg-orange-600 h-12 text-lg text-white"
                    disabled={loading || !paymentMethod || (paymentMethod === 'wallet' && !canPayWallet)}
                    data-testid="confirm-order-btn"
                  >
                    {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Procesando...</> :
                      paymentMethod === 'wallet' ? `Pagar con billetera ${formatPrice(total)}` : 'Confirmar pedido'}
                  </Button>
                )}
                {step === STEP_PAYMENT && paymentMethod === 'card' && (
                  <p className="text-xs text-gray-400 text-center mt-4">Completa los datos de tarjeta arriba para pagar</p>
                )}
                {step === STEP_SHIPPING && (
                  <p className="text-xs text-gray-400 text-center mt-4">Completa tu dirección para continuar al pago</p>
                )}
              </Card>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
